import { ApplicationRoleCode } from '@kcftech/prometheus-client';
import { defaultInputPadding, TextInput } from 'components/forms/TextInput';
import { useUser } from 'contexts/UserContext';
import { getUniqueUserInfo, sortIssueUserInfo } from 'pages/Desk/issues/shared';
import { IssueUserInfo } from 'pages/Desk/issues/types';
import { UsersPopupContent } from 'pages/Desk/issues/UsersPopupContent';
import { UserTag, UserTagWrapper } from 'pages/Desk/issues/UserTag';
import { useIssueUsers } from 'pages/Desk/issues/useIssueUsers';
import * as React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { TriggeredPopup } from 'shared/triggered_popup/TriggeredPopup';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { lastItem, toggleArrayItem } from 'utils/array';
import { stopEvent } from 'utils/event';

export const UserInputWrapper = styled.div`
    padding: 0 1rem;
    font-size: 1.4rem;
    border-radius: 3px;
    border: solid ${tokens.nav.border} 2px;
    background-color: ${tokens.background.default};
    cursor: text;

    &:focus-within {
        border: solid ${tokens.input.borderFocus} 2px;
    }
`;

export const UserInputStyledInput = styled(TextInput)`
    outline: none;
    border: none;
    width: 100%;
    font-size: 1.4rem;
    padding: ${defaultInputPadding} 0;
`;

export const UserInputTags = styled.div`
    max-height: 26rem;
    overflow-y: scroll;
    overflow-x: hidden;
    display: flex;
    flex-direction: row;
    row-gap: 0.5rem;
    column-gap: 1rem;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: start;
    margin-top: 0.5rem;
`;

export type UsersInputProps = Readonly<{
    locationId: string;
    placeholder: string;
    selectedUsers: IssueUserInfo[];
    onSelectedUsersChange: (users: IssueUserInfo[]) => void;
    className?: string;
    isInternal?: boolean;
}>;

export function UsersInput({
    locationId,
    placeholder,
    selectedUsers,
    onSelectedUsersChange,
    className,
    isInternal = false,
}: UsersInputProps) {
    const [filterText, setFilterText] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const { user: currentUser } = useUser();

    const { users, isLoading, hasNextPage, fetchNextPage } = useIssueUsers({
        locationId,
        isEnabled: true,
        searchText: filterText,
        isInternal,
    });

    const inputRef = useRef<HTMLInputElement>(null);
    const triggerRef = useRef<HTMLInputElement>(null);

    const selectedUserIds = useMemo(
        () => selectedUsers.map((user) => user.id),
        [selectedUsers],
    );

    const sortedAndUniqueUsers = useMemo(
        () => sortIssueUserInfo(getUniqueUserInfo(selectedUsers)),
        [selectedUsers],
    );

    const usersToRender = useMemo<IssueUserInfo[]>(() => {
        if (!users) {
            return [];
        }
        const filteredUsers = users.filter((user) => !selectedUserIds.includes(user.id));
        return sortIssueUserInfo(getUniqueUserInfo(filteredUsers));
    }, [selectedUserIds, users]);

    const reset = useCallback(() => {
        setFilterText('');
        setHighlightedIndex(0);
    }, []);

    const toggleUserSelection = useCallback(
        (userToToggle: IssueUserInfo) => {
            reset();
            onSelectedUsersChange(toggleArrayItem(selectedUsers, userToToggle));
        },
        [reset, onSelectedUsersChange, selectedUsers],
    );

    const canRemoveUser = (userToRemove: IssueUserInfo) =>
        currentUser &&
        // Current user is not a regular user
        (currentUser.ApplicationRole !== ApplicationRoleCode.User ||
            // User to remove is not a System Admin or Sales user
            (userToRemove.role !== ApplicationRoleCode.GlobalAdmin &&
                userToRemove.role !== ApplicationRoleCode.Sales));

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Backspace' && !filterText && selectedUsers.length > 0) {
            stopEvent(event);
            onSelectedUsersChange(
                selectedUsers.filter((user) => user !== lastItem(selectedUsers)),
            );
        }
        if (!filterText) {
            return;
        }
        if (event.key === 'Escape') {
            reset();
            stopEvent(event);
        } else if (event.key === 'ArrowDown') {
            stopEvent(event);
            setHighlightedIndex((prevIndex) =>
                prevIndex < usersToRender.length - 1 ? prevIndex + 1 : 0,
            );
        } else if (event.key === 'ArrowUp') {
            stopEvent(event);
            setHighlightedIndex((prevIndex) =>
                prevIndex > 0 ? prevIndex - 1 : usersToRender.length - 1,
            );
        } else if (event.key === 'Enter') {
            stopEvent(event);
            if (highlightedIndex in usersToRender) {
                reset();
                onSelectedUsersChange([
                    ...selectedUsers,
                    usersToRender[highlightedIndex],
                ]);
            }
        }
    };

    const onInputTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilterText(event.target.value);
        setHighlightedIndex(0);
    };

    return (
        <>
            <UserInputWrapper
                tabIndex={0}
                role="textbox"
                className={className}
                ref={triggerRef}
                onFocus={() => inputRef.current?.focus()}
            >
                {sortedAndUniqueUsers.length > 0 && (
                    <UserInputTags>
                        {sortedAndUniqueUsers.map((user) => (
                            <UserTag
                                key={user.id}
                                user={user}
                                onRemove={
                                    canRemoveUser(user)
                                        ? () => toggleUserSelection(user)
                                        : undefined
                                }
                            />
                        ))}
                    </UserInputTags>
                )}
                <UserInputStyledInput
                    ref={inputRef}
                    placeholder={placeholder}
                    value={filterText}
                    onChange={onInputTextChange}
                    onKeyDown={onKeyDown}
                />
            </UserInputWrapper>

            {filterText && (
                <TriggeredPopup
                    anchorRef={triggerRef}
                    onClose={reset}
                    noPadding
                    showArrow={false}
                    offsetDistance={0}
                    matchWidth
                    test-id="UsersInput__UsersPopupContent"
                >
                    <UsersPopupContent
                        users={usersToRender}
                        searchText={filterText}
                        isLoading={isLoading}
                        hasMore={hasNextPage}
                        onBottom={fetchNextPage}
                        highlightedIndex={highlightedIndex}
                        onUserSelect={toggleUserSelection}
                    />
                </TriggeredPopup>
            )}
        </>
    );
}

export const StyledUserInput = styled(UsersInput)`
    margin-top: 0.5rem;
    background-color: ${tokens.background.subtle} !important;

    ${UserTagWrapper} {
        background-color: ${tokens.background.default} !important;
    }
`;
